import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import {
    Alert,
    Snackbar,
    Tooltip,
    Typography,
    Box,
    Fade,
    IconButton,
} from "@mui/material"
import React, { useContext, useEffect, useState } from "react"

import { TT_DRAFT_ID_HELP } from "../../../../constants/tooltips"
import HelpIcon from "../../../helper/helpIcon"
import { ApplicationContext } from "../applicationProvider"

interface DraftSaveIndicatorProps {
    showPermanent?: boolean
}

const defaultProps = {
    showPermanent: false,
}

/**
 * Displays a notification when a draft has been saved
 * Shows the draft ID and provides a copy button
 */
const DraftSaveIndicator: React.FC<DraftSaveIndicatorProps> = ({
    showPermanent = false,
}) => {
    const { draftId } = useContext(ApplicationContext)
    const [open, setOpen] = useState(false)
    const [copied, setCopied] = useState(false)

    // Fix the timer cleanup
    useEffect(() => {
        if (draftId) {
            setOpen(true)

            // Only set timeout if not permanent
            if (!showPermanent) {
                const timer = setTimeout(() => {
                    setOpen(false)
                }, 6000)

                // Return cleanup function
                return () => {
                    clearTimeout(timer)
                }
            }
        }
        return undefined
    }, [draftId, showPermanent])

    const handleClose = (): void => {
        setOpen(false)
    }

    const handleCopy = (): void => {
        if (draftId) {
            navigator.clipboard.writeText(draftId)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    if (!draftId) return null
    if (showPermanent) {
        return (
            <Alert
                severity="success"
                icon={<CheckCircleOutlineIcon />}
                sx={{ mb: 2, display: "flex", alignItems: "center" }}
            >
                <Box
                    sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}
                >
                    <Typography variant="body2" sx={{ mr: 1 }}>
                        Draft saved with ID:
                    </Typography>
                    <Typography
                        variant="body2"
                        fontWeight="bold"
                        component="span"
                        sx={{ mr: 1 }}
                    >
                        {draftId}
                    </Typography>
                    <Tooltip title={copied ? "Copied!" : "Copy draft ID"}>
                        <IconButton
                            onClick={handleCopy}
                            data-testid="copy-button"
                            size="small"
                        >
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <HelpIcon tooltip={TT_DRAFT_ID_HELP} />
                </Box>
            </Alert>
        )
    }

    // For temporary notifications
    return (
        <Snackbar
            open={open}
            autoHideDuration={6000}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            TransitionComponent={Fade}
        >
            <Alert
                severity="success"
                onClose={handleClose}
                sx={{ display: "flex", alignItems: "center" }}
            >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>
                        Draft saved with ID:
                    </Typography>
                    <Typography
                        variant="body2"
                        fontWeight="bold"
                        component="span"
                        sx={{ mr: 1 }}
                    >
                        {draftId}
                    </Typography>
                    <Tooltip title={copied ? "Copied!" : "Copy draft ID"}>
                        <IconButton
                            onClick={handleCopy}
                            data-testid="copy-button"
                            size="small"
                        >
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <HelpIcon tooltip={TT_DRAFT_ID_HELP} />
                </Box>
            </Alert>
        </Snackbar>
    )
}

DraftSaveIndicator.defaultProps = defaultProps

export default DraftSaveIndicator
